import React, { useState, useRef, useEffect } from "react";
import { Sparkle, PaperPlaneTilt, X, ChatCircleDots, Headset, CircleNotch } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";

export default function AIChatbot() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { from: "bot", text: "Hi! I'm EventVexa's AI assistant. Ask me about bookings, tickets, refunds, checkpoints or organizing." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sid, setSid] = useState(null);
  const [canEscalate, setCanEscalate] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setMsgs((m) => [...m, { from: "user", text }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/support/chat", { message: text, session_id: sid });
      setSid(data.session_id);
      setCanEscalate(!!data.escalate);
      setMsgs((m) => [...m, { from: "bot", text: data.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { from: "bot", text: "Sorry, I hit an error. You can connect with our support team." }]);
      setCanEscalate(true);
    } finally { setBusy(false); }
  };

  const escalate = async () => {
    const lastUser = [...msgs].reverse().find((m) => m.from === "user");
    try {
      await api.post("/ai/support/escalate", {
        subject: "Help request from AI assistant",
        message: lastUser?.text || "I need help from a support agent.",
        session_id: sid,
      });
      toast.success("Connected — our team will reply on your support tickets.");
      setMsgs((m) => [...m, { from: "bot", text: "I've created a support ticket for you. Our team will get back to you shortly. You can track it under Help & Support." }]);
      setCanEscalate(false);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} data-testid="ai-chat-toggle"
        className="fixed bottom-5 right-5 z-50 grid place-items-center w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-xl btn-press hover:scale-105 transition-transform">
        {open ? <X size={24} weight="bold" /> : <ChatCircleDots size={26} weight="fill" />}
      </button>

      {open && (
        <div data-testid="ai-chat-panel"
          className="fixed bottom-24 right-5 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] flex flex-col rounded-2xl border border-border bg-card shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4">
          <div className="bg-foreground text-background px-4 py-3 flex items-center gap-2">
            <span className="grid place-items-center w-8 h-8 rounded-full bg-primary"><Sparkle weight="fill" size={16} /></span>
            <div>
              <div className="font-heading font-semibold text-sm">EventVexa Assistant</div>
              <div className="text-[11px] text-background/60">AI-powered · replies in seconds</div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                <div data-testid={`ai-msg-${m.from}`}
                  className={`max-w-[82%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${m.from === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted text-foreground rounded-bl-sm"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="px-3 py-2 rounded-2xl bg-muted"><CircleNotch size={16} className="animate-spin text-primary" /></div></div>}
            {canEscalate && (
              <button onClick={escalate} data-testid="ai-chat-escalate"
                className="mx-auto flex items-center gap-1.5 text-xs font-semibold text-primary border border-primary/30 rounded-full px-3 py-1.5 hover:bg-primary/5">
                <Headset size={14} /> Talk to a human
              </button>
            )}
            <div ref={endRef} />
          </div>

          <div className="p-2.5 border-t border-border flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type your question…" className="h-10" data-testid="ai-chat-input" />
            <Button onClick={send} disabled={busy} className="h-10 w-10 p-0 rounded-full shrink-0" data-testid="ai-chat-send">
              <PaperPlaneTilt weight="fill" size={16} />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
